<script>
import {useToast} from "vue-toastification";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import AssetMaterialSelectDialog from "@/AssetMaterialSelectDialog.vue";
import assetMaterialsHelper from "@/mixins/assetMaterialsHelper";

export default {
  components: {
    VueDatePicker,
    AssetMaterialSelectDialog
  },
  props: [
    'asset_id',
    'client_id',
    'activity_id',
    'activity_type',
  ],
  mixins: [
    assetMaterialsHelper
  ],
  async mounted() {
    // Setup toast
    this.toast = useToast()
    // Get CSRF token
    await this.requestGetCSRFtoken();
    // Get asset types
    await this.getAssetTypes('ovl');
    // Get asset materials
    await this.requestGetAssetMaterials(this.asset_id);
  },
  data () {
    return {
      toast: null,
      materials: [],
      materialsLoading: true,
      btnInstallDateLoading: false,
      btnReplaceLoading: false,
      btnDeleteLoading: false,
      datepickerActive: true,
      selectMaterialDialog: false,
      selectMaterialAssetType: '', // For example: 'ovl_pole' or 'ovl_lamp'
      selectMaterialTheMaterialToBeReplaced: null,
      actions: [],
    }
  },
  methods: {
    openReplaceByDialog(material) {
      this.selectMaterialDialog = true;
      this.selectMaterialAssetType = material.type;
      this.selectMaterialTheMaterialToBeReplaced = material;
    },
    closeReplaceByDialog() {
      this.selectMaterialDialog = false;
      this.selectMaterialTheMaterialToBeReplaced = null;
      this.selectMaterialAssetType = '';
    },
    materialSelected(selected_material) {
      // Get the original material we want to replace
      const material = this.selectMaterialTheMaterialToBeReplaced;
      // Replace the material
      this.requestReplaceByMaterial(material, selected_material.selected_material);
      // Close the dialog
      this.closeReplaceByDialog();
    },
    removeHighlight(material) {
      // Get the index of the material
      const index = this.getMaterialIndex(material);
      // Remove 'highlight' property after 5 seconds
      setTimeout(function(scope) {
        scope.materials[index].highlight = false;
      }, 5000, this);
    },
    async revertMaterialReplace(material) {
      this.startLoadingMaterial(material);
      this.btnReplaceLoading = true;
      // Send request to revert the revision
      const response = await this.requestRevertMaterialToRevision(material, material.lastActionRevisionID);
      // Show status/toast messages
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;
      if (response.status === 201) {
        this.toast.success(`${material_type_name} '${material_name}' vervanging is teruggezet.`);
        // Get the newly updated material
        let new_material = await response.json();
        // Update the material asset
        this.updateAssetMaterial(material, new_material);
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' vervanging kon niet teruggezet worden. Foutcode: [${response.status}] ${response.statusText}`);
      }
      this.stopLoadingMaterial(material);
      this.btnReplaceLoading = false;
    },
    async revertMaterialDelete(material) {
      this.startLoadingMaterial(material);
      this.btnDeleteLoading = true;
      // Send request to revert the revision
      const response = await this.requestRevertMaterialToRevision(material, material.lastActionRevisionID);
      // Show status/toast messages
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;
      if (response.status === 201) {
        this.toast.success(`${material_type_name} '${material_name}' verwijdering is teruggezet.`);
        // Get the newly updated material
        let new_material = await response.json();
        // Update the material asset
        this.updateAssetMaterial(material, new_material);
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' verwijdering kon niet teruggezet worden. Foutcode: [${response.status}] ${response.statusText}`);
      }
      this.stopLoadingMaterial(material);
      this.btnDeleteLoading = false;
    },
    updateAssetMaterial(material, new_material) {
      // Get the index of the material
      const index = this.getMaterialIndex(material);
      // Highlight the row
      new_material.highlight = true;
      // Remove the highlight after 5 seconds
      this.removeHighlight(material);
      // Replace it in the materials array
      this.materials[index] = new_material;
      // Return it so we can alter it again
      return this.materials[index];
    },
    startLoadingMaterial(material) {
      // Get the index of the material
      const index = this.getMaterialIndex(material);
      this.materials[index].loading = true;
    },
    stopLoadingMaterial(material) {
      // Get the index of the material
      const index = this.getMaterialIndex(material);
      this.materials[index].loading = false;
    },
    getMaterialIndex(material) {
      // Get the index of the material
      return this.materials.findIndex(item => item.id === material.id);
    },
    async requestDeleteMaterial(material) {
      this.startLoadingMaterial(material);
      this.btnDeleteLoading = true;
      // Remove the material
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/' + this.asset_id + '/materials/' + material.id, {
        method: 'DELETE',
        credentials: "include",
        headers: this.requestHeaders,
        body: JSON.stringify({
          action: 'delete',
          activity_id: this.activity_id,
          activity_type: this.activity_type
        })
      })

      const status_code = response.status;
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;

      if (status_code === 204) {
        this.toast.success(`${material_type_name} '${material_name}' is verwijderd.`);
        // Mark the material as deleted by changing the status
        material.status = 'Demolished';
        // Highlight the row
        material.highlight = true;
        // Save the last revision ID
        material.lastActionRevisionID = material.revision_id;
        // Remove the highlight after 5 seconds
        this.removeHighlight(material);
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' kon niet verwijderd worden. Foutcode: [${response.status}] ${response.statusText}`);
      }
      this.stopLoadingMaterial(material);
      this.btnDeleteLoading = false;
    },
    async requestReplaceMaterial(material) {
      this.startLoadingMaterial(material);
      this.btnReplaceLoading = true;
      // Save the revision ID of the current material before replacing it. We need this when reverting.
      let lastRevisionID = material.revision_id;
      // Replace material with the same material
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/' + this.asset_id + '/materials/' + material.id, {
        method: 'PATCH',
        credentials: "include",
        headers: this.requestHeaders,
        body: JSON.stringify({
          action: 'replace',
          activity_id: this.activity_id,
          activity_type: this.activity_type
        })
      })

      const status_code = response.status;
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;

      if (status_code === 201) {
        this.toast.success(`${material_type_name} '${material_name}' is vervangen met hetzelfde materiaal.`);
        // Get the newly updated material
        let new_material = await response.json();
        // Update the material asset
        this.updateAssetMaterial(material, new_material);
        // Mark it as replaced
        new_material.hasBeenReplaced = true;
        // Save the last revision ID
        new_material.lastActionRevisionID = lastRevisionID;
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' kon niet vervangen worden. Foutcode: [${response.status}] ${response.statusText}`);
      }

      this.stopLoadingMaterial(material);
      this.btnReplaceLoading = false;
    },
    async requestReplaceByMaterial(material, new_material) {
      this.startLoadingMaterial(material);
      this.btnReplaceLoading = true;
      // Save the revision ID of the current material before replacing it. We need this when reverting.
      let lastRevisionID = material.revision_id;
      // Replace material by other material
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/' + this.asset_id + '/materials/' + material.id, {
        method: 'PATCH',
        credentials: "include",
        headers: this.requestHeaders,
        body: JSON.stringify({
          action: 'replace-by',
          new_material_id: new_material.id,
          activity_id: this.activity_id,
          activity_type: this.activity_type
        })
      })

      const status_code = response.status;
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;

      if (status_code === 201) {
        this.toast.success(`${material_type_name} '${material_name}' is vervangen.`);
        // Get the newly updated material
        let new_material = await response.json();
        // Update the material asset
        this.updateAssetMaterial(material, new_material);
        // Mark it as replaced
        new_material.hasBeenReplaced = true;
        // Save the last revision ID
        new_material.lastActionRevisionID = lastRevisionID;
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' kon niet vervangen worden. Foutcode: [${response.status}] ${response.statusText}`);
      }
      this.stopLoadingMaterial(material);
      this.btnReplaceLoading = false;
    },
    async requestChangeInstallDate(material) {
      this.startLoadingMaterial(material);
      this.btnInstallDateLoading = true;
      // Save the revision ID of the current material before replacing it. We need this when reverting.
      let lastRevisionID = material.revision_id;
      // Change install date
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/' + this.asset_id + '/materials/' + material.id, {
        method: 'PATCH',
        credentials: "include",
        headers: this.requestHeaders,
        body: JSON.stringify({
          action: 'change-install-date',
          install_date: material.install_date,
          activity_id: this.activity_id,
          activity_type: this.activity_type
        })
      })

      const status_code = response.status;
      const material_type_name = this.assetTypes[material.type];
      const material_name = material.database.label;

      if (status_code === 201) {
        this.toast.success(`${material_type_name} '${material_name}' plaatsingsdatum is gewijzigd.`);
        // Get the newly updated material
        let new_material = await response.json();
        // Update the material asset
        this.updateAssetMaterial(material, new_material);
        // Save the last revision ID
        material.lastActionRevisionID = lastRevisionID;
      }
      else {
        this.toast.error(`${material_type_name} '${material_name}' plaatsingsdatum kon niet gewijzigd worden. Foutcode: [${response.status}] ${response.statusText}`);
      }

      this.stopLoadingMaterial(material);
      this.btnInstallDateLoading = false;
    },
    async requestRevertMaterialToRevision(material, revision_id) {
      // Revert material to a specific revision ID
      return await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/' + this.asset_id + '/materials/' + material.id, {
        method: 'PATCH',
        credentials: "include",
        headers: this.requestHeaders,
        body: JSON.stringify({
          action: 'revert-to-revision',
          revision_id: revision_id,
          activity_id: this.activity_id,
          activity_type: this.activity_type
        })
      })
    },
    async requestGetAssetMaterials(asset_id) {
      this.materialsLoading = true;
      // Fetch session token
      const response = await fetch(import.meta.env.VITE_API_BASEURL + 'api/asset/'+ asset_id +'/materials/', {
        credentials: "include"
      })
      const response_json = await response.json();
      // Set materials data
      this.materials = response_json.materials;
      if (response_json.actions) {
        this.actions = response_json.actions;
      }
      this.materialsLoading = false;
    },
  },
}
</script>


<template>

  <v-skeleton-loader v-if="materialsLoading" type="table-tbody"></v-skeleton-loader>

  <v-table v-if="!materialsLoading" class="materials-table">
    <thead>
    <tr>
      <th class="text-left">
        Naam
      </th>
      <th class="text-left">
        Type
      </th>
      <th class="text-left">
        Nummer
      </th>
      <th class="text-left">
        Plaatsingsdatum
      </th>
    </tr>
    </thead>
    <tbody>
    <tr
      v-for="material in materials"
      :key="material.id"
      :class="{
        'isDemolished': material.status === 'Demolished',
        'highlight': material.highlight,
      }"
    >
      <td>{{ material.database.label }}</td>
      <td>{{ assetTypes[material.type] }}</td>
      <td>{{ material.number }}</td>
      <td>
        {{ $filters.formatDateOnly(material.install_date) }}

        <v-btn
          v-if="actions['change-install-date']"
          variant="tonal"
          size="x-small"
          density="default"
          icon="mdi-pencil"
          :disabled="material.loading"
          :loading="material.loading && btnInstallDateLoading"
        >
          <v-icon>
            mdi-pencil
          </v-icon>
          <v-menu activator="parent">
            <vue-date-picker
              inline
              auto-apply
              :enable-time-picker="false"
              format="yyyy-MM-dd"
              locale="nl"
              model-type="format"
              v-model="material.install_date"
              @update:model-value="requestChangeInstallDate(material)"
            ></vue-date-picker>
          </v-menu>
        </v-btn>

      </td>
      <td>

        <template v-if="!material.hasBeenReplaced">
          <v-btn
              @click="requestReplaceMaterial(material)"
              :disabled="material.loading"
              :loading="material.loading && btnReplaceLoading"
          >Vervang</v-btn>

          <v-btn
            color="primary"
            :disabled="material.loading"
          >
            <v-icon size="large">mdi-menu-down</v-icon>

            <v-menu activator="parent">
              <v-list>
                <v-list-item key="replace" @click="requestReplaceMaterial(material)" :disabled="material.loading">
                  <v-list-item-title>Vervang met hetzelfde materiaal</v-list-item-title>
                </v-list-item>
                <v-list-item key="replaceBy" @click="openReplaceByDialog(material)" :disabled="material.loading">
                  <v-list-item-title>Vervang door ander materiaal..</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </template>

        <v-btn
          v-if="material.hasBeenReplaced"
          @click="revertMaterialReplace(material)"
          :disabled="material.loading"
          :loading="material.loading && btnReplaceLoading"
          color="warning">
          Vervanging terugzetten
        </v-btn>

      </td>
      <td>
        <v-btn
          v-if="material.status !== 'Demolished'"
          @click="requestDeleteMaterial(material)" color="error"
          :disabled="material.loading"
          :loading="material.loading && btnDeleteLoading"
        >
          Verwijder
        </v-btn>

        <v-btn
          v-if="material.status === 'Demolished'"
          @click="revertMaterialDelete(material)"
          :disabled="material.loading"
          :loading="material.loading && btnDeleteLoading"
          color="warning">
          Verwijdering terugzetten
        </v-btn>
      </td>
    </tr>
    </tbody>
  </v-table>


  <!-- Select material dialog -->
  <asset-material-select-dialog
    :client-id="client_id"
    :asset-type="selectMaterialAssetType"
    v-model="selectMaterialDialog"
    @material-selected="materialSelected"
    @dialog-closed="closeReplaceByDialog"
  ></asset-material-select-dialog>


</template>

<style lang="scss">

  @keyframes highlight {
    0% {
      background-color: rgba(255, 229, 0, 0.3)
    }
    50% {
      background-color: rgba(255, 229, 0, 0.3)
    }
    100% {
      background-color: rgba(255, 229, 0, 0);
    }
  }

  .materials-table {
    table {
      margin: 0;
    }
  }

  tr {
    &.isDemolished {
      text-decoration: line-through;
      opacity: 0.5;
    }
    &.highlight {
      animation: highlight 5s;
    }
  }
</style>
